/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import {
  LeadService,
  OrderService,
  AuthService,
  LeadOrderHelpersService,
} from '@services';
import {
  AgentLogged,
  CreateOrUpdateOrderItem,
  DataLead,
  DataLeadCard,
  LeadChatList,
  LeadDocumentType,
  LeadInformation,
  LeadOrder,
  NavListItemArchived,
  OrderShipping,
  Quote,
  ShippingItemTime,
  ShippingPaginationResult,
} from '@interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { convertDate, extractBusinessLines } from '@utils';
import { CommercialAgreementDialogComponent } from '../commercial-agreement-dialog/commercial-agreement-dialog.component';
import { MultiBusinessLineDialogComponent } from '../multi-business-line-dialog/multi-business-line-dialog.component';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { WebsocketHandlerService } from 'src/app/core/services/websocket';
import { REGEX_DATE_YYYY_MM_DD, Time } from '@constants';

@Component({
  selector: 'unicon-track-lead-dialog',
  templateUrl: './track-lead-dialog.component.html',
  styleUrl: './track-lead-dialog.component.scss',
})
export class TrackLeadDialogComponent implements OnInit, OnDestroy {
  protected dataLead: DataLeadCard;
  protected selectedArchivedType: NavListItemArchived | undefined;
  protected leadCardList: DataLeadCard[];
  protected lead!: LeadOrder;
  protected loadingLead: boolean = true;
  protected loadingChat: boolean = true;
  protected chatList!: LeadChatList;
  protected pageNumber: number = 1;
  private pageSize: number = 30;
  protected selectedImage?: string;
  protected isFullScreen: boolean = false;
  protected documentTypes: LeadDocumentType[] = [];
  protected hideAddButton: boolean = true;
  protected add: boolean = false;
  protected showQuote: boolean = false;
  protected quote?: Quote;
  protected orderId?: string;
  protected showCalendarGrid: boolean = false;
  protected calendarGridKey: number = 0;
  protected dateCalendarGrid?: string;
  protected calendarOrderId?: string;
  protected calendarGridBusinessLineId?: number;
  protected calendarGridBusinessLineName?: string;
  protected errorCalendar?: string;
  protected calendarGridResult?: ShippingPaginationResult;
  private resultDatesCalendar?: ShippingItemTime[];
  protected seller!: AgentLogged;
  protected isNotificationVisible: boolean = false;
  protected activeArchiveType!: (type: NavListItemArchived) => void;
  protected isActivated: boolean = false;
  protected loadingNote: boolean = false;
  protected editNote$ = new BehaviorSubject<boolean>(false);

  protected showCommercialAgreement: boolean = false;
  protected showMessageEmpty: boolean = false;
  protected businessLines: {
    businessLineId: number;
    businessLineName: string;
  }[] = [];

  protected showDialogCommercial: boolean = false;
  public dialogRefCommercial?: MatDialogRef<CommercialAgreementDialogComponent>;
  protected dateOrder$ = new BehaviorSubject<string>('');
  protected isLoadingQuote?: boolean;
  protected isLoadingSendQuote: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DataLead,
    public dialogRef: MatDialogRef<TrackLeadDialogComponent>,
    private leadService: LeadService,
    private orderService: OrderService,
    private authService: AuthService,
    private websocket: WebsocketHandlerService,
    private leadOrderHelpers: LeadOrderHelpersService,
    private dialog: MatDialog,
  ) {
    this.dataLead = data.lead;
    this.leadCardList = data.listCard;
    this.selectedArchivedType = data.archivedType;
    this.activeArchiveType = data.activeArchive;
  }

  ngOnInit(): void {
    this.initialServices();
  }

  protected onTopbarResult(event: string) {
    if (event === 'close') {
      this.dialogRef.close();
      return;
    }

    this.initialServices();
  }

  protected initialServices() {
    this.getLeadDocumentTypes();
    this.getLead(true);
    this.getHistoricChatList(this.pageNumber, this.pageSize);
    this.setSessionNames();
    this.listenReceiveUpdatedOrder();
    this.listenReceiveNewLeadMessages();
    if (this.selectedArchivedType) {
      this.isNotificationVisible = true;
    }
  }

  private setSessionNames(): void {
    const storedSession: AgentLogged | undefined =
      this.authService.getSessionValues();
    if (storedSession) {
      this.seller = storedSession;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected listenReceiveUpdatedOrder() {
    this.websocket
      .listen<any>('ReceiveUpdatedOrder')
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data]) => {
        if (data.id === this.dataLead.orderId) {
          this.lead = data;
          this.dataLead.assignedAgent = data.assignedAgent;
          // Recalcular businessLines cuando se actualizan items por WebSocket
          this.calculateBusinessLines();
        }
      });
  }

  protected listenReceiveNewLeadMessages() {
    this.websocket
      .listen<any>('ReceiveNewLeadMessages')
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data]) => {
        if (this.chatList) {
          data.isBlockedByAgent = this.chatList.isBlockedByAgent;
        }

        if (data.leadId === this.dataLead.leadId && data.messages.length > 0) {
          this.pageSize = this.pageSize + data.messages[0].items.length;
          if (this.chatList?.messages?.length && data.messages?.length) {
            data.messages = this.chatList.messages.concat(data.messages);
            this.chatList = data;
          }
        }
      });
  }

  private getLeadDocumentTypes(): void {
    this.leadService.getLeadDocumentTypes().subscribe({
      next: (documentTypes: LeadDocumentType[]) => {
        this.documentTypes = documentTypes;
      },
    });
  }

  private getLead(useLoading: boolean = false): void {
    if (useLoading) {
      this.loadingLead = true;
    }

    if (
      this.dataLead &&
      this.dataLead?.orderId &&
      this.dataLead?.orderId.length > 0
    ) {
      this.orderService.getOrder(this.dataLead?.orderId).subscribe({
        next: (lead: LeadOrder) => {
          this.lead = lead;
          this.isActivated = lead.isActivated;

          // Calcular businessLines una sola vez
          this.calculateBusinessLines();

          if (useLoading) {
            this.loadingLead = false;
          }
        },
        error: () => {
          if (useLoading) {
            this.loadingLead = false;
          }
        },
      });
    }
  }

  private getHistoricChatList(
    pageNumber: number,
    pageSize: number,
    useLoading: boolean = true,
  ): void {
    if (useLoading) {
      this.loadingChat = true;
    }

    if (!this.dataLead || !this.dataLead.leadId) {
      if (useLoading) {
        this.loadingChat = false;
      }
      return;
    }

    this.leadService
      .getLeadMessages(this.dataLead.leadId, pageNumber, pageSize)
      .subscribe({
        next: (chatList: LeadChatList) => {
          if (this.chatList && this.chatList?.leadId === chatList?.leadId) {
            chatList.messages = chatList.messages.concat(
              this.chatList.messages,
            );
          }
          this.chatList = chatList;

          if (useLoading) {
            this.loadingChat = false;
          }
        },
        error: () => {
          if (useLoading) {
            this.loadingChat = false;
          }
        },
      });
  }

  protected closeModal = (): void => {
    this.dialogRef.close();
  };

  protected closeModalArchiveLead() {
    this.closeModal();
    if (this.selectedArchivedType) {
      this.activeArchiveType(this.selectedArchivedType);
    }
  }

  protected onSubmit(formDataValue: FormData): void {
    this.orderService
      .saveMessage(this.dataLead.orderId, formDataValue)
      .subscribe();
  }

  protected onScrollUp(): void {
    if (this.pageNumber < this.chatList.lastPage) {
      this.pageNumber++;
      this.getHistoricChatList(this.pageNumber, this.pageSize, false);
    }
  }

  protected onImage(imageUrl: string): void {
    this.selectedImage = imageUrl;
  }

  protected fullscreen(): void {
    this.isFullScreen = !this.isFullScreen;
  }

  protected closeImageViewer(): void {
    this.selectedImage = undefined;
  }

  protected closeAllImageViewer(): void {
    this.fullscreen();
    this.closeImageViewer();
  }

  protected onIdentificationNumber(e: {
    inputType: string;
    input: string;
  }): void {
    this.updateLeadInformation({
      identificationDocumentNumber: e.input,
      identificationDocumentTypeId: parseInt(e.inputType, 10),
    });
  }

  protected onPhoneNumber(phone: string): void {
    this.updateLeadInformation({
      phone,
    });
  }

  protected onEmail(email: string): void {
    this.updateLeadInformation({
      email,
    });
  }

  protected onSendFullName(e: {
    leadNames: string;
    leadFirstSurname: string;
    leadSecondSurname: string;
  }) {
    this.updateLeadInformation({
      names: e.leadNames,
      firstSurname: e.leadFirstSurname,
      secondSurname: e.leadSecondSurname,
    });
  }

  protected onShippingResult(orderId: string, request: OrderShipping): void {
    this.orderService.updateOrderShipping(orderId, request).subscribe({
      error: (error: HttpErrorResponse) => {
        this.showCalendarGrid = false;
        this.errorCalendar = error.error.detail;
        setTimeout(() => {
          this.errorCalendar = undefined;
        }, Time.FIVE_SECONDS);
      },
    });
  }

  private getOrderAvailability(
    orderId: string,
    date: string,
    page: number = 1,
    pageNumber: number = 6,
  ): void {
    this.orderService
      .shippingAvailability(orderId, date, page, pageNumber)
      .subscribe({
        next: (data: ShippingPaginationResult) => {
          this.openCalendarGrid(orderId, date, data);
        },
        error: (error: HttpErrorResponse) => {
          this.showCalendarGrid = false;
          this.errorCalendar = error.error.detail;
          this.dateOrder$.next('');
          setTimeout(() => {
            this.errorCalendar = undefined;
          }, Time.FIVE_SECONDS);
        },
      });
  }

  protected orderAvailability(calendarOrderId: string, dateTime: string) {
    const dateFormat = REGEX_DATE_YYYY_MM_DD.test(dateTime)
      ? dateTime
      : convertDate(dateTime);
    this.getOrderAvailability(calendarOrderId, dateFormat);
  }

  protected onSetCalendarGrid(
    {
      date,
      businessLineId,
      businessLineName,
    }: {
      date: string;
      orderId: string;
      businessLineId: number;
      businessLineName: string;
    },
    calendarOrderId: string,
  ): void {
    this.calendarGridBusinessLineId = businessLineId;
    this.calendarGridBusinessLineName = businessLineName;

    const regexHour = /-\s\d{2}:\d{2}\s-\s\d{2}:\d{2}/;
    if (this.showDialogCommercial && this.dialogRefCommercial) {
      this.dialogRefCommercial.close();
    }

    const dateToUse = regexHour.test(date)
      ? date.substring(0, date.indexOf('-') - 1)
      : date;

    const dateFormat = REGEX_DATE_YYYY_MM_DD.test(dateToUse)
      ? dateToUse
      : convertDate(dateToUse);

    // Limpiar todos los datos e incrementar key para forzar recreación del componente
    this.dateCalendarGrid = undefined;
    this.calendarOrderId = undefined;
    this.calendarGridResult = undefined;
    this.resultDatesCalendar = undefined;
    this.calendarGridKey++;
    this.showCalendarGrid = true;

    // Hacer la petición
    this.orderService
      .shippingAvailability(calendarOrderId, dateFormat)
      .subscribe({
        next: (data: ShippingPaginationResult) => {
          this.openCalendarGrid(calendarOrderId, dateFormat, data);
        },
        error: (error: HttpErrorResponse) => {
          this.showCalendarGrid = false;
          this.errorCalendar = error.error.detail;
          this.dateOrder$.next('');
          setTimeout(() => {
            this.errorCalendar = undefined;
          }, Time.FIVE_SECONDS);
        },
      });
  }

  protected openCalendarGrid(
    calendarOrderId: string,
    date: string,
    data: ShippingPaginationResult,
  ): void {
    this.resultDatesCalendar =
      this.leadOrderHelpers.formatCalendarGridData(data);
    this.calendarGridResult = {
      ...data,
      results: this.resultDatesCalendar || [],
    };
    this.dateCalendarGrid = date;
    this.calendarOrderId = calendarOrderId;
  }

  protected onCloseCalendarGrid(): void {
    this.showCalendarGrid = !this.showCalendarGrid;
    this.dateCalendarGrid = undefined;
    this.calendarOrderId = undefined;
    this.calendarGridBusinessLineId = undefined;
    this.calendarGridBusinessLineName = undefined;
  }

  protected trackByGridKey(index: number, item: number): number {
    return item;
  }

  private updateLeadInformation(request: LeadInformation): void {
    this.leadService
      .updateLeadInformation(this.lead.leadId, request)
      .subscribe();
  }

  protected onDeleteProduct(orderItemId: string, leadOrderId: string): void {
    this.orderService.deleteOrderItem(leadOrderId, orderItemId).subscribe();
  }

  protected onEditProduct(
    event: { orderId: string; formValue: CreateOrUpdateOrderItem },
    leadOrderId: string,
  ): void {
    this.orderService
      .updateOrderItem(leadOrderId, event.orderId, event.formValue)
      .subscribe();
  }

  protected onAddProduct(
    formValue: CreateOrUpdateOrderItem,
    leadOrderId: string,
  ): void {
    this.addItem();
    this.orderService.createOrderItem(leadOrderId, formValue).subscribe();
  }

  protected onShowAddItemButton(show: boolean): void {
    this.hideAddButton = !show;
  }

  protected addItem(): void {
    this.add = !this.add;
  }

  protected onQuote(orderId: string, type?: string): void {
    // Verificar si hay múltiples unidades de negocio diferentes
    const uniqueBusinessLines = this.getUniqueBusinessLines();

    if (uniqueBusinessLines.length > 1) {
      // Mostrar modal de advertencia para múltiples unidades de negocio
      const dialogRef = this.dialog.open(MultiBusinessLineDialogComponent, {
        data: {
          businessLines: uniqueBusinessLines,
          orderName:
            'Orden #' + (this.lead?.orderId || this.lead?.id || orderId),
        },
        disableClose: false,
        autoFocus: false,
        width: 'auto',
        height: 'auto',
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          // Usuario confirmó con "Entendido", no proceder con la cotización
          // Solo cerrar el modal sin abrir QuoteDialogComponent
        } else {
          // Usuario canceló con "X", cerrar también el QuoteDialogComponent
          this.onCloseQuote();
        }
      });
    } else {
      // Solo una unidad de negocio, proceder normalmente
      this.executeQuoteAction(orderId, type);
    }
  }

  private executeQuoteAction(orderId: string, type?: string): void {
    this.leadOrderHelpers.handleQuoteAction(
      orderId,
      () => {
        this.isLoadingQuote = true;
      },
      (quote: Quote, orderId: string) => {
        this.isLoadingQuote = false;
        this.orderId = orderId;
        this.quote = quote;
      },
      () => this.onCloseQuote(),
      type,
    );
  }

  private getUniqueBusinessLines(): {
    businessLineId: number;
    businessLineName: string;
  }[] {
    if (!this.businessLines || this.businessLines.length === 0) {
      return [];
    }

    // Filtrar unidades de negocio únicas por businessLineName
    const uniqueNames = new Set();
    return this.businessLines.filter(bl => {
      if (uniqueNames.has(bl.businessLineName)) {
        return false;
      }
      uniqueNames.add(bl.businessLineName);
      return true;
    });
  }

  protected onSend(event: { orderId: string; fileTypeNames: string[] }): void {
    this.isLoadingSendQuote = true;
    this.orderService.sendQuote(event.orderId, event.fileTypeNames).subscribe({
      next: () => {
        this.isLoadingSendQuote = false;
        this.onCloseQuote();
      },
      error: () => {
        this.isLoadingSendQuote = false;
        this.onCloseQuote();
      },
    });
  }

  protected onCloseQuote(): void {
    this.showQuote = !this.showQuote;
    this.quote = undefined;
  }

  protected handleData(data: DataLeadCard | undefined) {
    if (!data) {
      return;
    }
    this.dataLead = data;
    this.getLead(true);
    this.getHistoricChatList(this.pageNumber, this.pageSize);
    this.setSessionNames();
  }

  protected readMessagesInChat(orderId: string) {
    this.orderService.updateReadMessages(orderId).subscribe();
  }

  protected onNextLead(type: string) {
    const orderIndex: number = this.leadCardList
      ? this.leadCardList?.findIndex(
          obj => obj['orderId'] === this.dataLead.orderId,
        )
      : 0;
    let nextOrderIndex: number;
    if (type === 'next') {
      nextOrderIndex = orderIndex + 1;
    } else {
      nextOrderIndex = orderIndex - 1;
    }
    const nextIndex =
      nextOrderIndex >= this.leadCardList!.length ? 0 : nextOrderIndex;
    if (nextIndex !== 0) {
      const nextOrder =
        this.leadCardList!.length > 0
          ? this.leadCardList![nextIndex]
          : undefined;
      if (nextOrder && nextOrder.orderId) {
        this.readMessagesInChat(nextOrder.orderId);
        this.orderService.getOrder(nextOrder.orderId).subscribe({
          next: (lead: any) => {
            const newLead = { ...lead, orderId: nextOrder.orderId };
            this.handleData(newLead);
          },
        });
      }
    }
  }

  protected onOpenAgreement() {
    this.leadOrderHelpers.handleOpenAgreement(
      this.lead,
      this.onSetCalendarGrid.bind(this),
      () => this.getLead(true),
      dialogRef => {
        this.showDialogCommercial = true;
        this.dialogRefCommercial = dialogRef;
      },
      () => {
        this.showMessageEmpty = true;
        setTimeout(() => {
          this.showMessageEmpty = false;
        }, Time.FIVE_SECONDS);
      },
    );
  }

  protected onCommercialAgreement(): void {
    this.onOpenAgreement();
  }

  protected onDownloadQuote(link: string) {
    this.leadOrderHelpers.openLinkInNewTab(link);
  }

  protected updateNote(form: any) {
    this.leadOrderHelpers.handleUpdateNote(
      this.dataLead?.orderId,
      form.value as string,
      () => {
        this.loadingNote = true;
      },
      () => {
        this.loadingNote = false;
        this.editNote$.next(false);
        form.enable();
      },
    );
  }

  protected isActivatedButton() {
    if (this.lead.commercialAgreement) {
      return (
        (this.lead.commercialAgreement?.code === null &&
          this.lead.commercialAgreement.creationErrorDetail === null) ||
        (this.lead.commercialAgreement.code !== null &&
          this.lead.commercialAgreement.creationErrorDetail === null)
      );
    }

    return false;
  }

  private calculateBusinessLines(): void {
    this.businessLines = extractBusinessLines(this.lead?.items);
  }

  public getItemsByBusinessLine(businessLineId: number) {
    if (!this.lead?.items) {
      return [];
    }
    return this.lead.items.filter(
      item => item.businessLineId === businessLineId,
    );
  }
}
